import { useEffect, useState } from "react"
import { startOfWeek, addDays, endOfWeek } from "date-fns"
import { useActivitiesStore } from "../../../stores/use-activities-store"
import ActivitiesWeeklyBoard from "../weekly/activities-weekly-board"
import useGetActivities from "../../../queries/use-get-activities"
import { getActivitiesOfFirstWeek } from "../../../entities/groupActivitiesByWeekday"
import useGetCalendar from "@/modules/calendar/queries/use-get-calendar"
import extractISODate from "@/modules/calendar/utils/extract-iso-date"

interface WeeklyActivitiesProps {
  scrollRef: React.RefObject<HTMLDivElement>
}

export default function WeeklyActivities({ scrollRef }: WeeklyActivitiesProps) {
  const { setActivities } = useActivitiesStore()
  const [isDragging, setIsDragging] = useState(false)

  const now = new Date()
  const startDate = startOfWeek(now, { weekStartsOn: 1 })
  const endDate = endOfWeek(addDays(startDate, 7), { weekStartsOn: 1 })

  const activitiesQuery = useGetActivities({
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  })

  const eventsQuery = useGetCalendar({
    startDate: extractISODate(startDate),
    endDate: extractISODate(endDate),
    category: "study_time",
  })

  useEffect(() => {
    if (activitiesQuery.status === "success") {
      setActivities(getActivitiesOfFirstWeek(activitiesQuery.data))
    }
  }, [activitiesQuery.status, activitiesQuery.data])

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const slider = scrollRef.current
    if (isDragging) return
    if (!slider) return

    slider.dataset.isDragging = "true"
    slider.dataset.startX = e.pageX.toString()
    slider.dataset.scrollLeft = slider.scrollLeft.toString()
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const slider = scrollRef.current
    if (!slider || slider.dataset.isDragging !== "true") return

    const startX = Number(slider.dataset.startX || 0)
    const scrollLeft = Number(slider.dataset.scrollLeft || 0)
    const xDiff = e.pageX - startX

    slider.scrollLeft = scrollLeft - xDiff
  }

  const handleMouseUp = () => {
    const slider = scrollRef.current
    if (slider) slider.dataset.isDragging = "false"
  }

  const handleIsDragging = (dragging: boolean) => {
    setIsDragging(dragging)
  }

  return (
    <div
      ref={scrollRef}
      className="no-scrollbar no-scrollbar flex h-full overflow-x-auto"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseUp}
      onMouseUp={handleMouseUp}
    >
      {activitiesQuery.isSuccess && eventsQuery.isSuccess && (
        <ActivitiesWeeklyBoard
          isDragging={isDragging}
          setIsDragging={handleIsDragging}
          events={eventsQuery.data.data.description}
        />
      )}
    </div>
  )
}
